import { useState } from "react";

import { useHistory } from "react-router";

import { Badge, Button, clsx, Icon, Select, useModal } from "@alpacahq/ui/old";

import { OPTIONS_TIERS } from "~/v2/constants";
import { Path } from "~/v2/path";
import { useIsOptionsOnboardingAvailable } from "../../optionsOptIn/util";
import type { Fields } from "./configurationForm";

const OPTION_LEVELS = [
  { value: 0, label: "Disabled" },
  { value: 1, label: "Level 1" },
  { value: 2, label: "Level 2" },
  { value: 3, label: "Level 3" },
];

const OPTIONS_MODAL_TEXT = [
  {
    targetLevel: 0,
    text: "You won't be able to trade options.",
  },
  {
    targetLevel: 1,
    text: "You will be able to trade Covered Calls and Cash-Secured Puts.",
  },
  {
    targetLevel: 2,
    text: "You will be able to trade Long Calls and Long Puts.",
  },
  {
    targetLevel: 3,
    text: "You will be able to trade Spreads, Covered Straddles, and Multi-leg Strategies.",
  },
];

type OptionSectionProps = {
  onSubmit: (data: Partial<Fields>) => void;
  isDisabled: boolean;
};

export const OptionSection = ({ onSubmit, isDisabled }: OptionSectionProps) => {
  const history = useHistory();
  const [targetLevel, setTargetLevel] = useState<number>(-1);
  const {
    isOptionsOnboardingAvailable: isOptionsApplyAvailable,
    optionsApprovalStatus,
    optionsApprovedLevel,
    effectiveOptionsLevel,
  } = useIsOptionsOnboardingAvailable();

  const optionsConfirmationTexts = OPTIONS_MODAL_TEXT.find(
    (text) => targetLevel === text.targetLevel,
  );

  const { component: Modal, toggle } = useModal({
    size: "sm",
    title: `${
      targetLevel > 0 ? `Update to Level ${targetLevel}` : "Disable Options"
    }`,
    content: optionsConfirmationTexts?.text,
    controls: (
      <div className="flex w-full gap-4">
        <Button variant="alternate" className="w-full" onClick={() => toggle()}>
          Cancel
        </Button>
        <Button
          className="w-full"
          onClick={() => {
            onSubmit({
              max_options_trading_level: optionsConfirmationTexts?.targetLevel,
            });
            toggle();
          }}
        >
          Update
        </Button>
      </div>
    ),
  });

  const onOptionSelect = (targetLevel: number) => {
    setTargetLevel(targetLevel);
    toggle();
  };

  const optionLevels = OPTION_LEVELS.filter(
    ({ value }) => value <= optionsApprovedLevel,
  );

  return (
    <>
      <div className="mt-4 space-y-4">
        <div className="flex items-end justify-between border-b-2 p-2">
          <div>Options</div>

          <Badge variant={effectiveOptionsLevel > 0 ? "yellow" : "gray"}>
            {effectiveOptionsLevel > 0
              ? `Level ${effectiveOptionsLevel}`
              : "Disabled"}
          </Badge>
        </div>

        {(optionsApprovalStatus?.OptionsRequestStatus ===
          "LOWER_LEVEL_APPROVED" ||
          optionsApprovedLevel === 1) &&
          isOptionsApplyAvailable && (
            <div className="flex flex-col items-center justify-center gap-4 rounded bg-gray-100 p-6">
              Want to trade more options strategies? Click here and send your
              application today!
              <Button
                onClick={() =>
                  history.push(Path.ROUTE_OPTIONS_OPT_IN_FINANCIAL_PROFILE)
                }
              >
                Apply to Upgrade Your Options Level
              </Button>
            </div>
          )}

        <Select
          onChange={onOptionSelect}
          value={effectiveOptionsLevel}
          options={optionLevels}
          disabled={isDisabled}
        />

        <div className="space-y-1">
          {Object.entries(OPTIONS_TIERS).map(([stringLevel, items]) => {
            const level = Number(stringLevel);

            return items.map((item) => (
              <div key={item} className="flex items-center">
                <Icon
                  name={effectiveOptionsLevel >= level ? "Check" : "XMark"}
                  className={clsx(
                    effectiveOptionsLevel >= level
                      ? "text-yellow-400"
                      : "text-gray-400",
                    "mr-2 [&>path]:stroke-[4]",
                  )}
                />
                {item}
              </div>
            ));
          })}
        </div>
      </div>

      {Modal}
    </>
  );
};
